/**
 * 服务端消息行 → UI 消息的映射工具。
 *
 * <p>用于：
 * <ul>
 *   <li>切会话时从 {@code /api/ai/chat/sessions/{id}/messages} 拉历史并装入 store</li>
 *   <li>把 assistant + 紧随其后的 tool 工具行合并为单条带 timeline 的助手气泡</li>
 * </ul>
 * </p>
 */
import { markRaw } from 'vue'
import { resolveAssistantVisibleText } from '@/utils/aiMessageDisplay'
import type {
  AiMessageView,
  AiTimelineSegment,
  AiToolCallUi,
  AiUiMessage,
  CollapsedPriorRounds
} from './types'
import { compactTimeline } from './timeline'
import { isTimelineVisibleToolCall, hasExplicitBusinessFailure } from './detectors'

function toolRowToCallUi(row: AiMessageView): AiToolCallUi {
  const err = row.toolError
  const argumentView = row.toolArgumentsViewJson ?? row.toolArgumentsJson ?? '{}'
  const resultView = row.toolResultViewJson ?? row.toolResultJson ?? null
  const displayDisposition = isInternalRepairPayload(row.toolResultJson, err)
    ? 'INTERNAL_REPAIR'
    : 'VISIBLE'
  return {
    callId: row.toolCallId ?? '',
    toolName: row.toolName ?? '',
    displayLabel: row.toolDisplayLabel ?? null,
    argumentsJson: argumentView,
    // 业务失败判定与执行期 SSE 路径一致：工具行无异常但结果含 success:false 时同样是失败
    success: !err && !hasExplicitBusinessFailure(resultView),
    resultJson: resultView,
    errorMessage: err,
    durationMs: 0,
    displayDisposition,
    pending: false,
    capabilityKind: row.toolCapabilityKind,
    argumentViewJson: argumentView,
    resultViewJson: resultView
  }
}

/**
 * 落库的工具结果不带 SSE 的 displayDisposition 字段，从网关信封里恢复同一种
 * 无害重试分类，避免刷新后把 schema/preflight 修复渲染成红色业务失败。
 */
function isInternalRepairPayload(resultJson: string | null | undefined, error: string | null | undefined): boolean {
  const payload = `${resultJson ?? ''}\n${error ?? ''}`
  // output_truncated：模型输出被 max_tokens / 参数预算截断，调用未执行，由模型自行精简重试。
  return /(?:"gateway"\s*:\s*"|\b)(?:schema_mismatch|bad_arguments|PRECHECK_REJECTED|PREFLIGHT_REQUIRED|tool_not_exposed|output_truncated)\b/.test(payload)
}

function removeInternalRepairSegments(segments: AiTimelineSegment[]): void {
  const visible = segments.filter(
    (segment) => segment.kind !== 'tool' || segment.call.displayDisposition !== 'INTERNAL_REPAIR'
  )
  segments.splice(0, segments.length, ...visible)
}

/** Confirmation cards own pre-execution and non-execution states after refresh too. */
function removeNonExecutedConfirmationSegments(segments: AiTimelineSegment[]): void {
  const visible = segments.filter(
    (segment) => segment.kind !== 'tool' || isTimelineVisibleToolCall(segment.call)
  )
  segments.splice(0, segments.length, ...visible)
}

/**
 * 折叠态：判断一行是否是『最终 ACTIVE 消息』（不是 ROUND_REPLY 快照）。
 */
function isFinalAssistantRow(r: AiMessageView): boolean {
  return r.role === 'assistant' && r.status !== 'ROUND_REPLY'
}

/**
 * 折叠态：判断一行是否是『中间轮 ROUND_REPLY 快照』。
 */
function isRoundReplyRow(r: AiMessageView): boolean {
  return r.role === 'assistant' && r.status === 'ROUND_REPLY'
}

/**
 * 把单个 assistant 行展开为 timeline 段（reasoning + content）。
 * 紧随其后的 tool 行由调用方循环追加。返回 [nextIndex, 段数组]。
 */
function expandAssistantRowToSegments(
  slice: AiMessageView[],
  i: number
): { nextIndex: number; segments: AiTimelineSegment[] } {
  const r = slice[i]
  const out: AiTimelineSegment[] = []
  const rs = r.reasoningContent?.trim()
  if (rs) {
    out.push({ kind: 'reasoning', text: rs })
  }
  const ct = r.content?.trim()
  if (ct) {
    const visible = resolveAssistantVisibleText(ct)
    if (visible) {
      out.push({ kind: 'content', text: visible })
    }
  }
  return { nextIndex: i + 1, segments: out }
}

function mergeServerAssistantTurn(rows: AiMessageView[], start: number, endExclusive: number): AiUiMessage | null {
  const slice = rows.slice(start, endExclusive)
  if (slice.length === 0) {
    return null
  }

  // 折叠态：识别 final 助手行（最后一个不是 ROUND_REPLY 的 assistant）。
  // - 没有 ROUND_REPLY 行的回合：final 就是最后一条 assistant，不折叠
  // - 有 ROUND_REPLY 行的正常结束：final 是最后一个非 ROUND_REPLY 行
  // - 异常结束 / 没有 final 行的回合：finalAssistantIdx = -1，走兜底
  let finalAssistantIdx = -1
  for (let i = slice.length - 1; i >= 0; i--) {
    if (isFinalAssistantRow(slice[i])) {
      finalAssistantIdx = i
      break
    }
  }

  const finalRow = finalAssistantIdx >= 0 ? slice[finalAssistantIdx] : null
  const hasRoundReply = slice.some(isRoundReplyRow)
  // 折叠开关：final 存在 + final 带 taskDurationMs + 至少有一条 ROUND_REPLY
  const canCollapse = !!(finalRow && (finalRow.taskDurationMs ?? 0) > 0 && hasRoundReply)

  const mainTimeline: AiTimelineSegment[] = []
  const priorTimeline: AiTimelineSegment[] = []

  if (canCollapse && finalAssistantIdx >= 0) {
    // 折叠模式：前 N-1 轮 ROUND_REPLY + 它们的 tool 走 priorTimeline
    let i = 0
    while (i < finalAssistantIdx) {
      const r = slice[i]
      if (isRoundReplyRow(r)) {
        const { nextIndex, segments } = expandAssistantRowToSegments(slice, i)
        priorTimeline.push(...segments)
        i = nextIndex
        continue
      }
      if (r.role === 'tool') {
        priorTimeline.push({ kind: 'tool', call: toolRowToCallUi(r) })
        i++
        continue
      }
      // 兜底：跳过不可识别行
      i++
    }
    // final 行（以及后续 tool）走主 timeline
    let j = finalAssistantIdx
    while (j < slice.length) {
      const r = slice[j]
      if (r.role === 'assistant') {
        // final 行：content 就是最后一轮的用户可见回复，直接取用
        if (j === finalAssistantIdx && canCollapse) {
          const { segments } = expandAssistantRowToSegments(slice, j)
          mainTimeline.push(...segments)
          j++
          continue
        }
        const { nextIndex, segments } = expandAssistantRowToSegments(slice, j)
        mainTimeline.push(...segments)
        j = nextIndex
        continue
      }
      if (r.role === 'tool') {
        mainTimeline.push({ kind: 'tool', call: toolRowToCallUi(r) })
        j++
        continue
      }
      j++
    }
  } else {
    // 兜底模式（异常结束 / 缺少 taskDurationMs）：按原逻辑展开全部
    let i = 0
    while (i < slice.length) {
      const r = slice[i]
      if (r.role === 'assistant') {
        const { nextIndex, segments } = expandAssistantRowToSegments(slice, i)
        mainTimeline.push(...segments)
        i = nextIndex
        continue
      }
      if (r.role === 'tool') {
        mainTimeline.push({ kind: 'tool', call: toolRowToCallUi(r) })
        i++
        continue
      }
      i++
    }
  }

  removeInternalRepairSegments(mainTimeline)
  removeInternalRepairSegments(priorTimeline)
  removeNonExecutedConfirmationSegments(mainTimeline)
  removeNonExecutedConfirmationSegments(priorTimeline)

  const assistantRows = slice.filter((x) => x.role === 'assistant')
  if (assistantRows.length === 0 && slice.every((x) => x.role === 'tool')) {
    const onlyTools: AiTimelineSegment[] = slice.map((t) => ({
      kind: 'tool' as const,
      call: toolRowToCallUi(t)
    })).filter((segment) => isTimelineVisibleToolCall(segment.call))
    const firstT = slice[0]
    const lastT = slice[slice.length - 1]
    return {
      id: `srv-merge-tools-${firstT.id}-${lastT.id}`,
      role: 'assistant',
      content: '',
      reasoningContent: null,
      createdAt: new Date(firstT.createdAt).getTime(),
      timeline: onlyTools
    }
  }

  // Final visible text always comes from finalRow, especially in collapsed mode.
  // 兜底模式下退化到最后一个有内容的 assistant 行
  const lastAssistantText = (() => {
    if (finalRow) {
      const visible = resolveAssistantVisibleText(finalRow.content ?? '')
      if (visible) return visible
    }
    let text = ''
    for (const a of assistantRows) {
      const visible = resolveAssistantVisibleText(a.content)
      if (visible) {
        text = visible
      }
    }
    return text
  })()

  const first = slice[0]
  const last = slice[slice.length - 1]
  const mergedId = `srv-merge-${first.id}-${last.id}`

  const lastReasoning = finalRow?.reasoningContent?.trim()
    ? finalRow.reasoningContent
    : [...assistantRows].reverse().find((a) => a.reasoningContent?.trim())?.reasoningContent ?? null

  // 构建 collapsedPriorRounds（仅在 canCollapse=true 时）
  const collapsedPriorRounds: CollapsedPriorRounds | null = canCollapse && finalRow
    ? {
        durationMs: finalRow.taskDurationMs as number,
        segments: compactTimeline(priorTimeline)
      }
    : null

  const compactedMain = compactTimeline(mainTimeline)
  return {
    id: mergedId,
    role: 'assistant',
    content: lastAssistantText,
    reasoningContent: lastReasoning,
    createdAt: new Date(first.createdAt).getTime(),
    timeline: compactedMain.length > 0 ? compactedMain : null,
    // 正常结束的折叠态携带 collapsedPriorRounds；空折叠（没有 prior）则不挂载
    collapsedPriorRounds: collapsedPriorRounds && collapsedPriorRounds.segments.length > 0
      ? collapsedPriorRounds
      : null,
    // 正常结束的回合标记 normal；没有 collapsedPriorRounds 视为 unknown
    runStatus: collapsedPriorRounds ? 'normal' : null
  }
}

/** 助手回合是否有用户可见内容（避免仅含内部 plan/bridge 时渲染空白气泡）。 */
function hasVisibleAssistantTurn(msg: AiUiMessage): boolean {
  if (msg.error?.trim()) {
    return true
  }
  if (resolveAssistantVisibleText(msg.content || '').trim()) {
    return true
  }
  if (msg.collapsedPriorRounds?.segments?.length) {
    return true
  }
  if (!msg.timeline?.length) {
    return false
  }
  return msg.timeline.some((seg) => {
    if (seg.kind === 'reasoning') {
      return seg.text.trim().length > 0
    }
    if (seg.kind === 'tool') {
      return true
    }
    if (seg.kind === 'content') {
      return resolveAssistantVisibleText(seg.text).trim().length > 0
    }
    return false
  })
}

/** 判断服务端 user 消息行是否为「主代理内部桥接文本」。
 *  直接使用后端权威字段 {@link AiMessageView.isSystemBridge}，
 *  避免靠文本前缀识别而误伤用户手动输入的真实消息。 */
function isBridgeUserRow(row: AiMessageView): boolean {
  return row.role === 'user' && row.isSystemBridge === true
}

/** 服务端按 id 升序拉平的消息 → 与流式单气泡一致的 UI 列表 */
function coalesceServerMessagesForUi(rows: AiMessageView[]): AiUiMessage[] {
  const out: AiUiMessage[] = []
  let i = 0
  while (i < rows.length) {
    const r = rows[i]
    if (r.role === 'user') {
      if (isBridgeUserRow(r)) {
        i++
        continue
      }
      const serverAttachments = r.materialAttachments && r.materialAttachments.length > 0
        ? r.materialAttachments.map(a => ({ materialId: a.materialId, fileName: a.fileName }))
        : undefined
      out.push({
        id: `srv-${r.id}`,
        role: 'user',
        content: r.content ?? '',
        materialAttachments: serverAttachments,
        isSystemBridge: r.isSystemBridge ?? false,
        createdAt: new Date(r.createdAt).getTime()
      })
      i++
      continue
    }

    if (r.role === 'assistant' || r.role === 'tool') {
      const start = i
      // Skip historical internal bridge rows. They are not real user turn boundaries.
      // 否则 N 条 ROUND_REPLY 会被切成 N 个独立气泡，最终 final 无法折叠所有历史。
      while (i < rows.length) {
        const cur = rows[i]
        if (cur.role === 'user' && !isBridgeUserRow(cur)) {
          break
        }
        i++
      }
      const merged = mergeServerAssistantTurn(rows, start, i)
      if (merged && hasVisibleAssistantTurn(merged)) {
        out.push(merged)
      }
      continue
    }

    i++
  }
  // 性能债修复(热点2)：时间线是整体读取的展示数据，无组件依赖片段内部的细粒度
  // 响应式；标记为原始数据让 Vue 跳过深度代理，避免历史装载/对账时深层代理成本。
  for (const msg of out) {
    if (msg.timeline && msg.timeline.length > 0) {
      markRaw(msg.timeline)
    }
  }
  return out
}

export {
  toolRowToCallUi,
  mergeServerAssistantTurn,
  coalesceServerMessagesForUi,
  hasVisibleAssistantTurn,
  isInternalRepairPayload
}
